// Empresas → Holdings: los holdings del operador y la ficha de cada uno.
//
// La ficha junta lo que necesita la nota consolidada del holding, cada parte contra el servicio
// que es su dueño: el interruptor "Cobro consolidado" con su checklist (billing), datos fiscales
// (contracts), contacto y plazo (billing), tarifa con "vigente desde" (billing), empresas con su
// membresía e inconsistencias (contracts) y las credenciales de la API del holding
// (integrations). Deep-link
// ?tab=holdings&holding=<id> (el asistente de cobro enlaza acá cuando un holding no se puede
// emitir); adminContractsView limpia ?holding= al cambiar de pestaña.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowLeft, Building2, Coins, FileText, KeyRound, Network, Phone } from "lucide-react";
import useUrlState from "../../../../hooks/useUrlState";
import { Badge } from "../../../../components/ui/Badge";
import { Button } from "../../../../components/ui/Button";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { Skeleton } from "../../../../components/ui/Skeleton";
import { SectionCard } from "../../../../components/ui/SectionCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../components/ui/Tabs";
import { commerceSecondaryLine } from "../../../../utils/commerceSearch";
import { holdingErrorText, missingFieldsText } from "../../cobro/utils/holdings";
import { loadHoldingsWithConsolidation } from "../../cobro/utils/operatorHoldingsWithConsolidation";
import HoldingFiscalForm from "../../cobro/components/holdings/HoldingFiscalForm";
import HoldingCollectionProfileForm from "../../cobro/components/holdings/HoldingCollectionProfileForm";
import HoldingPricingSection from "./HoldingPricingSection";
import HoldingCompaniesSection from "./HoldingCompaniesSection";
import HoldingConsolidationSection from "./HoldingConsolidationSection";
import HoldingApiCredentialsSection from "./HoldingApiCredentialsSection";

const DETAIL_TABS = ["fiscal", "contacto", "tarifa", "empresas", "api"];

// Cuántas diferencias de membresía reparables tiene (no bloquean el cobro).
const repairableCount = (inc = {}) =>
  ["missingInList", "missingHoldingId", "danglingHoldingId", "duplicateIds", "orphanIds"].reduce(
    (n, key) => n + (Array.isArray(inc[key]) ? inc[key].length : 0),
    Number(inc.invalidEntries) || 0,
  );

/**
 * Badges de estado de un holding en la lista y en la cabecera de la ficha. El del cobro
 * consolidado solo sale si se pudo leer de billing (`consolidatedEnabled` boolean).
 */
function HoldingStatusBadges({ holding }) {
  const { t } = useTranslation();
  const conflicts = holding?.inconsistencies?.conflicts?.length ?? 0;
  const missing = holding?.missingFiscalFields?.length ?? 0;
  const repairable = repairableCount(holding?.inconsistencies);
  return (
    <span className="flex flex-wrap gap-1.5">
      <Badge tone="info">{t("{{n}} empresas", { n: holding?.companies?.length ?? 0 })}</Badge>
      {holding?.consolidatedEnabled === true && (
        <Badge tone="success">{t("Cobro consolidado encendido")}</Badge>
      )}
      {holding?.consolidatedEnabled === false && (
        <Badge tone="neutral">{t("Cobro consolidado apagado: se cobran sueltas")}</Badge>
      )}
      {conflicts > 0 && <Badge tone="error">{t("Conflicto de membresía")}</Badge>}
      {missing > 0 && <Badge tone="warning">{t("Faltan datos fiscales")}</Badge>}
      {repairable > 0 && <Badge tone="neutral">{t("Membresía por reparar")}</Badge>}
    </span>
  );
}

function HoldingDetail({ holding, holdingNameOf, onBack, onFiscalSaved, onConsolidationChanged, onReload }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("fiscal");
  // Sube al guardar el perfil o la tarifa: el checklist del cobro consolidado los relee.
  const [readinessKey, setReadinessKey] = useState(0);
  const bumpReadiness = useCallback(() => setReadinessKey((k) => k + 1), []);
  const secondary = commerceSecondaryLine(holding);
  const missing = holding.missingFiscalFields ?? [];

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        {t("Volver a los holdings")}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <Network className="h-5 w-5 text-fg-muted" aria-hidden />
            {holding.name || t("Sin nombre")}
          </h2>
          {secondary && <p className="text-sm text-fg-muted">{secondary}</p>}
        </div>
        <HoldingStatusBadges holding={holding} />
      </div>

      <HoldingConsolidationSection
        holding={holding}
        refreshKey={readinessKey}
        onGoToSection={(section) => setTab(DETAIL_TABS.includes(section) ? section : "fiscal")}
        onChanged={onConsolidationChanged}
      />

      <Tabs value={tab} onValueChange={(v) => setTab(DETAIL_TABS.includes(v) ? v : "fiscal")}>
        <TabsList ariaLabel={t("Secciones del holding")}>
          <TabsTrigger value="fiscal" icon={<FileText />}>
            {t("Datos fiscales")}
          </TabsTrigger>
          <TabsTrigger value="contacto" icon={<Phone />}>
            {t("Contacto y plazo")}
          </TabsTrigger>
          <TabsTrigger value="tarifa" icon={<Coins />}>
            {t("Tarifa")}
          </TabsTrigger>
          <TabsTrigger value="empresas" icon={<Building2 />}>
            {t("Empresas")}
          </TabsTrigger>
          <TabsTrigger value="api" icon={<KeyRound />}>
            {t("API")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="fiscal" className="mt-4">
          <SectionCard
            title={t("Datos fiscales")}
            subtitle={
              missing.length > 0
                ? t("Falta para emitir la nota del holding: {{fields}}.", {
                    fields: missingFieldsText(missing, t),
                  })
                : t("El receptor de la nota consolidada del holding.")
            }
          >
            <HoldingFiscalForm key={holding.id} holding={holding} onSaved={onFiscalSaved} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="contacto" className="mt-4">
          <SectionCard
            title={t("Contacto y plazo")}
            subtitle={t("A quién se le envía la nota del holding y en cuántos días vence.")}
          >
            <HoldingCollectionProfileForm holdingId={holding.id} onSaved={bumpReadiness} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="tarifa" className="mt-4">
          <SectionCard title={t("Tarifa")} subtitle={t("Con qué precios se cobra la nota consolidada.")}>
            <HoldingPricingSection holdingId={holding.id} onSaved={bumpReadiness} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="empresas" className="mt-4">
          <SectionCard
            title={t("Empresas")}
            subtitle={t("Las empresas que se cobran dentro de la nota del holding.")}
          >
            <HoldingCompaniesSection
              holding={holding}
              holdingNameOf={holdingNameOf}
              consolidatedEnabled={holding.consolidatedEnabled}
              onLinked={onReload}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="api" className="mt-4">
          <SectionCard
            title={t("API del holding")}
            subtitle={t("Credenciales para que el sistema del holding cree sus empresas por API.")}
          >
            <HoldingApiCredentialsSection holding={holding} />
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Pestaña "Holdings" de Empresas: lista de los holdings del operador (GET contracts/holdings)
 * y la ficha del elegido.
 */
export default function HoldingsPanel() {
  const { t } = useTranslation();
  const [holdings, setHoldings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // billing no respondió el estado del cobro consolidado: la lista sale sin ese dato.
  const [consolidationError, setConsolidationError] = useState(null);
  const [selectedId, setSelectedId] = useUrlState("holding", "", { push: true });
  const seq = useRef(0);

  const load = useCallback(async () => {
    const current = ++seq.current;
    setLoading(true);
    setLoadError(null);
    try {
      const result = await loadHoldingsWithConsolidation();
      if (current !== seq.current) return;
      setHoldings(result.holdings);
      setConsolidationError(result.consolidationError);
    } catch (err) {
      if (current === seq.current) setLoadError(err);
    } finally {
      if (current === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      seq.current += 1;
    };
  }, [load]);

  const byId = useMemo(
    () => new Map((holdings ?? []).map((h) => [String(h.id), h])),
    [holdings],
  );
  const holdingNameOf = useCallback((id) => byId.get(String(id))?.name ?? null, [byId]);

  // Los datos fiscales vuelven en la respuesta del PATCH: se aplican sin releer la lista.
  const handleFiscalSaved = (view) => {
    if (!view?.id) return;
    setHoldings((list) =>
      (list ?? []).map((h) => (String(h.id) === String(view.id) ? { ...h, ...view } : h)),
    );
  };

  // El interruptor devuelve el estado nuevo: la lista y la ficha lo reflejan sin releer.
  const handleConsolidationChanged = ({ holdingId, consolidatedEnabled, consolidatedEnabledAt }) => {
    setHoldings((list) =>
      (list ?? []).map((h) =>
        String(h.id) === String(holdingId) ? { ...h, consolidatedEnabled, consolidatedEnabledAt } : h,
      ),
    );
  };

  const consolidationWarning = consolidationError && (
    <div
      className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-warning-soft px-3 py-2 text-sm text-status-warning"
      role="alert"
    >
      <span>{t("No se pudo consultar qué holdings tienen el cobro consolidado encendido.")}</span>
      <Button size="sm" variant="outline" onClick={load}>
        {t("Reintentar")}
      </Button>
    </div>
  );

  if (loading && !holdings) {
    return (
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2" role="status" aria-live="polite">
        <span className="sr-only">{t("Cargando…")}</span>
        {[0, 1].map((i) => (
          <Skeleton key={i} className="h-24 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (loadError && !holdings) {
    return (
      <div className="rounded-lg border border-border bg-bg" role="alert">
        <EmptyState
          icon={<AlertTriangle />}
          title={t("No se pudieron cargar los holdings.")}
          description={holdingErrorText(loadError, t, "Reintentá en unos minutos.")}
          action={
            <Button variant="outline" onClick={load}>
              {t("Reintentar")}
            </Button>
          }
        />
      </div>
    );
  }

  if (selectedId) {
    const selected = byId.get(String(selectedId));
    if (!selected) {
      return (
        <div className="rounded-lg border border-border bg-bg">
          <EmptyState
            icon={<Network />}
            title={t("El holding no existe o no es de tu operador.")}
            action={
              <Button variant="outline" onClick={() => setSelectedId("")}>
                {t("Volver a los holdings")}
              </Button>
            }
          />
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {consolidationWarning}
        <HoldingDetail
          holding={selected}
          holdingNameOf={holdingNameOf}
          onBack={() => setSelectedId("")}
          onFiscalSaved={handleFiscalSaved}
          onConsolidationChanged={handleConsolidationChanged}
          onReload={load}
        />
      </div>
    );
  }

  if ((holdings ?? []).length === 0) {
    return (
      <div className="rounded-lg border border-border bg-bg">
        <EmptyState
          icon={<Network />}
          title={t("Tu operador no tiene holdings.")}
          description={t("Un holding agrupa varias empresas que se cobran juntas, en una sola nota. Para crear uno, contactá a soporte de Beartrack.")}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {consolidationWarning}
      <ul className="grid grid-cols-1 gap-3 md:grid-cols-2" aria-label={t("Holdings del operador")}>
        {holdings.map((h) => {
        const secondary = commerceSecondaryLine(h);
        return (
          <li key={h.id}>
            <button
              type="button"
              onClick={() => setSelectedId(String(h.id))}
              className="w-full rounded-xl border border-border bg-bg p-4 text-left transition-colors hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-fg">
                <Network className="h-4 w-4 text-fg-muted" aria-hidden />
                {h.name || t("Sin nombre")}
              </span>
              {secondary && <span className="mt-0.5 block text-xs text-fg-muted">{secondary}</span>}
              <span className="mt-2 block">
                <HoldingStatusBadges holding={h} />
              </span>
            </button>
          </li>
        );
      })}
      </ul>
    </div>
  );
}
